import { useEffect, useCallback } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { routesPaths } from './constants/paths';
import './locale/initI18';
import Login from './pages/Auth/Login';
import Mobile from './pages/Auth/Mobile';
import Register from './pages/Auth/Register';
import ResetPassword from './pages/Auth/ResetPassword';
import SetPassword from './pages/Auth/SetPassword';
import ProjectDashboard from './pages/ProjectDashboard/ProjectDashboard';
import Incident from './pages/Incident/Incident';
import Inspection from './pages/Inspection/Inspection';
import Observation from './pages/Observation/Observation';
import ProtectedRoute from './pages/ProtectedRoutes';
import AuthProtectedRoute from './pages/AuthProtectedRoute';
import {
  onLogin,
  onLogout,
  categories,
  organizations,
  saveUserDetails,
  setInviteSource,
  setSourceEmail,
} from './redux/authSlice';
import { useUserDetailsMutation } from './redux/services/auth';
import Projects from './pages/Projects/Projects';
import ProjectSetup from './pages/Projects/ProjectSetup';
import { getHeaders } from 'helper/helper';
import { RootStates } from 'redux/store';
import Checklist from 'pages/Checklist/Checklist';
import ChecklistSetup from 'pages/Checklist/ChecklistSetup';
import Users from 'pages/Users/Users';
import {
  deleteInviteToken,
  getCurrentInviteToken,
  setInviteToken,
} from 'helper/inviteTokenHelper';
import { Onboarding } from 'pages/Onboarding';
import { ChangePassword } from 'pages/ChangePassword';
import ConductInspection from 'pages/ConductInspection';
import ScheduleInspection from 'pages/ScheduleInspection';
import LogObservation from 'pages/LogObservation';
import RecordIncident from 'pages/RecordIncident';
import ScheduledInspections from 'pages/ScheduledInspections';
import ReviewInspection from 'pages/ReviewInspection';
import Help from 'pages/Help';
import ReviewIncident from 'pages/ReviewIncident';
import ReviewObservation from 'pages/ReviewObservation';
import Plans from 'pages/Plans';
import EditProfile from 'pages/EditProfile';
import Tasks from 'pages/Tasks';
import EditSubProjects from 'pages/EditProjectDetails';
import { Dashboard } from 'pages/Dashboard';
import './assets/css/style.scss';
import { AppDemo } from 'pages/AppDemo';
import { Splashscreen } from 'components/Splashscreen';
import { Hidden } from '@mui/material';

function App() {
  const user = useSelector((state: RootStates) => state.auth);
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop.toString()),
  });
  const token = (params as any).inviteToken;
  if (token) {
    setInviteToken(token);
  }
  const source = (params as any).source;
  const email = (params as any).email;

  const [userDetails] = useUserDetailsMutation();
  const dispatch = useDispatch();

  const verifyToken = useCallback(async () => {
    const headers = getHeaders();
    if (navigator.onLine) {
      const inviteToken = getCurrentInviteToken()?.inviteToken;
      const isVerify: any = await userDetails({
        headers,
        inviteToken,
        source,
        email,
      });

      if (isVerify.data) {
        const { inviteMessage, inviteSuccess } = isVerify.data;
        if (inviteMessage) {
          toast[inviteSuccess ? 'success' : 'error'](inviteMessage);
          deleteInviteToken();
        }
        dispatch(onLogin());
        dispatch(saveUserDetails(isVerify.data));
        dispatch(categories(isVerify.data.categories));
        dispatch(organizations(isVerify.data.organizations));
      } else {
        toast.error(isVerify.error.data.message);
        dispatch(onLogout());
      }
    } else {
      toast.error('No Internet Connection');
    }
  }, [dispatch, email, source, userDetails]);
  useEffect(() => {
    if (user.isLoggedIn) {
      verifyToken();
    }
  }, [user.isLoggedIn, verifyToken]);

  useEffect(() => {
    if (source) {
      dispatch(setInviteSource(source));
      dispatch(setSourceEmail(email));
    }
  }, [source, email, dispatch]);

  return (
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#363636',
            color: '#fff',
          },
          duration: 2500,
        }}
      />
      <Hidden mdUp>
        <Splashscreen />
      </Hidden>
      <Routes>
        <Route path="/" element={<Navigate to={routesPaths.login} replace />} />
        <Route element={<AuthProtectedRoute />}>
          <Route path={routesPaths.register} element={<Register />} />
          <Route path={routesPaths.login} element={<Login />} />
          <Route path={routesPaths.mobile} element={<Mobile />} />
          <Route path={routesPaths.setPassword} element={<SetPassword />} />
          <Route path={routesPaths.reset} element={<ResetPassword />} />
        </Route>
        <Route element={<ProtectedRoute />}>
          <Route path={routesPaths.help} element={<Help />} />
          <Route path={routesPaths.inspection} element={<Inspection />} />
          <Route path={routesPaths.observation} element={<Observation />} />
          <Route path={routesPaths.incident} element={<Incident />} />
          <Route path={routesPaths.projects} element={<Projects />} />
          <Route path={routesPaths.createProject} element={<ProjectSetup />} />
          <Route path={routesPaths.projectSetup} element={<ProjectSetup />} />
          <Route path={routesPaths.checklist} element={<Checklist />} />
          <Route
            path={routesPaths.changePassword}
            element={<ChangePassword />}
          />
          <Route
            path={routesPaths.conductInspection}
            element={<ConductInspection />}
          />
          <Route
            path={routesPaths.conductInspectionSetup}
            element={<ConductInspection />}
          />
          <Route
            path={routesPaths.scheduleInspection}
            element={<ScheduleInspection />}
          />
          <Route
            path={routesPaths.scheduledInspections}
            element={<ScheduledInspections />}
          />
          <Route
            path={routesPaths.logObservation}
            element={<LogObservation />}
          />
          <Route
            path={routesPaths.addObservation}
            element={<LogObservation />}
          />
          <Route
            path={routesPaths.recordIncident}
            element={<RecordIncident />}
          />
          <Route path={routesPaths.addIncident} element={<RecordIncident />} />
          <Route
            path={routesPaths.checklistSetup}
            element={<ChecklistSetup />}
          />
          <Route
            path={routesPaths.reviewInspection}
            element={<ReviewInspection />}
          />
          <Route
            path={routesPaths.reviewIncident}
            element={<ReviewIncident />}
          />
          <Route
            path={routesPaths.reviewObservation}
            element={<ReviewObservation />}
          />
          <Route path={routesPaths.users} element={<Users />} />
          <Route
            path={routesPaths.projectDashboard}
            element={<ProjectDashboard />}
          />
          <Route path={routesPaths.dashboard} element={<Dashboard />} />
          <Route path={routesPaths.profile} element={<EditProfile />} />
          <Route path={routesPaths.plans} element={<Plans />} />
          <Route path={routesPaths.allTasks} element={<Tasks />} />
          <Route path={routesPaths.projectTasks} element={<Tasks />} />
          <Route path={routesPaths.inspectionTasks} element={<Tasks />} />
          <Route path={routesPaths.observationTasks} element={<Tasks />} />
          <Route path={routesPaths.incidentTasks} element={<Tasks />} />
          <Route
            path={routesPaths.scheduledInspectionTasks}
            element={<Tasks />}
          />
          <Route
            path={routesPaths.editSubProjects}
            element={<EditSubProjects section="subProjects" />}
          />
          <Route
            path={routesPaths.editChecklist}
            element={<EditSubProjects section="checklist" />}
          />
          <Route
            path={routesPaths.editDocuments}
            element={<EditSubProjects section="documents" />}
          />
          <Route
            path={routesPaths.editTeams}
            element={<EditSubProjects section="teams" />}
          />
        </Route>
        <Route element={<ProtectedRoute useLayout={false} />}>
          <Route path={routesPaths.onboarding} element={<Onboarding />} />
          <Route path={routesPaths.onboardingPage} element={<Onboarding />} />
          <Route path={routesPaths.appDemo} element={<AppDemo />} />
        </Route>
        <Route path="*" element="Page not found" />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
